import React from 'react';

interface PlotVblConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  plotType: 'probability' | 'cdf';
  onPlotTypeChange: (type: 'probability' | 'cdf') => void;
  showConfidenceIntervals: boolean;
  onToggleConfidenceIntervals: (value: boolean) => void;
  confidenceLevel: number;
  onConfidenceLevelChange: (value: number) => void;
}

/**
 * Plot VBL Configuration Modal - Settings for VBL plot display
 * Mirrors PlotConfigModal exactly but for VBL plots
 */
export const PlotVblConfigModal: React.FC<PlotVblConfigModalProps> = ({
  isOpen,
  onClose,
  plotType,
  onPlotTypeChange,
  showConfidenceIntervals,
  onToggleConfidenceIntervals,
  confidenceLevel,
  onConfidenceLevelChange,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Plot Configuration</h3>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Plot Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Plot Type
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="probability"
                  checked={plotType === 'probability'}
                  onChange={(e) => onPlotTypeChange(e.target.value as 'probability' | 'cdf')}
                  className="mr-2"
                />
                <span className="text-sm">Weibull Probability Plot</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="cdf"
                  checked={plotType === 'cdf'}
                  onChange={(e) => onPlotTypeChange(e.target.value as 'probability' | 'cdf')}
                  className="mr-2"
                />
                <span className="text-sm">Cumulative Distribution Function (CDF)</span>
              </label>
            </div>
          </div>

          {/* Confidence Intervals */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showConfidenceIntervals}
                onChange={(e) => onToggleConfidenceIntervals(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">Show Confidence Intervals</span>
            </label>
          </div>

          {/* Confidence Level */}
          {showConfidenceIntervals && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confidence Level: {confidenceLevel}%
              </label>
              <input
                type="range"
                min="90"
                max="99"
                step="1"
                value={confidenceLevel}
                onChange={(e) => onConfidenceLevelChange(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>90%</span>
                <span>95%</span>
                <span>99%</span>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};